import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { paymentService } from '../services';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { PromotionCodeDetails } from '../types/api';
import { startTransition } from 'react';

// LocalStorage key for promotion code
const PROMO_CODE_STORAGE_KEY = 'piika_promo_code';

// Type for the subscription store state
interface SubscriptionState {
  paymentType: string;
  currency: string;
  subscriptionType: string;
  updateSubscriptionState: (newState: any) => void;
}

// Extended promotion details type to include planDetails for price calculations
interface ExtendedPromotionDetails extends PromotionCodeDetails {
  isLoaded: boolean;
  planDetails?: {
    premium?: { price?: number };
    standard?: { price?: number };
  };
  durationInMonths?: number | null;
}

// Type for subscription type
type SubscriptionType = 'monthly' | 'yearly' | 'trial';

export const usePromotionCode = () => {
  // Use individual selectors instead of an object selector to prevent infinite loops
  const paymentType = useSubscriptionStore(useCallback(state => state.paymentType, []));
  const currency = useSubscriptionStore(useCallback(state => state.currency, []));
  const subscriptionType = useSubscriptionStore(useCallback(state => state.subscriptionType, []));
  const updateSubscriptionStateFn = useSubscriptionStore(useCallback(state => state.updateSubscriptionState, []));

  // State for loading/error status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for promotion code
  const [promotionCode, setPromotionCode] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem(PROMO_CODE_STORAGE_KEY) || '';
  });
  const [promotionDetails, setPromotionDetails] = useState<ExtendedPromotionDetails>({
    isLoaded: false,
    isValid: false,
    percentOff: 0,
    amountOff: 0,
    couponId: '',
    description: '',
    allowedPaymentTypes: [],
    duration: 'once',
    durationInMonths: null,
    minimumAmount: 0, 
    currency: null
  });

  // Custom setter for promotion code that also updates localStorage
  const setPromoCodeWithStorage = useCallback((code: string) => {
    setPromotionCode(code);
    // Save to localStorage when setting a new code
    if (code) {
      localStorage.setItem(PROMO_CODE_STORAGE_KEY, code);
    } else {
      localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
    }
  }, []);
  
  // Memoized updateSubscriptionState function
  const updateSubscriptionState = useMemo(
    () => updateSubscriptionStateFn,
    [updateSubscriptionStateFn]
  );

  // Helper function to calculate price
  const calculatePrice = (price: number) => {
    // Apply discount if valid
    if (promotionDetails.isValid) {
      if (promotionDetails.percentOff > 0) {
        return price * (1 - promotionDetails.percentOff / 100);
      }
      if (promotionDetails.amountOff > 0) {
        return Math.max(0, price - promotionDetails.amountOff);
      }
    }
    return price;
  };

  // Track if we've shown the promotion toast
  const [hasShownPromoToast, setHasShownPromoToast] = useState(false);
  // Track if we've shown payment type warning
  const [hasShownPaymentSwitchToast, setHasShownPaymentSwitchToast] = useState(false);
  
  // Effect to fetch and process promotion code details
  useEffect(() => {
    // Skip if no promotion code or already loaded
    if (!promotionCode || promotionDetails.isLoaded) {
      return;
    }
    
    // Add a flag to prevent duplicate calls
    let isApiCallInProgress = false;
    let isMounted = true; // Track if component is still mounted
    
    // Use a static flag to prevent duplicate calls across hook instances
    if ((window as any).__promotionCodeFetchInProgress) {
      return;
    }
    
    // Set global flag to prevent other instances from fetching simultaneously
    (window as any).__promotionCodeFetchInProgress = true;
    
    const fetchPromotionDetails = async () => {
      // Skip if API call is already in progress
      if (isApiCallInProgress) {
        return;
      }
      
      try {
        // Set flag to indicate API call is in progress
        isApiCallInProgress = true;
        
        // Set loading state to true first
        setPromotionDetails({
          isLoaded: false,
          isValid: false,
          percentOff: 0,
          amountOff: 0,
          couponId: '',
          description: 'Loading...',
          allowedPaymentTypes: [],
          duration: 'once',
          durationInMonths: null,
          minimumAmount: 0,
          currency: null
        });
        
        // Call an API to get promotion code details
        const response = await paymentService.getPromotionCodeDetails(promotionCode);
        
        // Only process if still mounted
        if (!isMounted) return;
        
        if (response && response.isValid) {
          // Check if currencies match or if the promotion code doesn't have currency restriction
          const currencyMatches = !response.currency || response.currency === currency;
          
          if (currencyMatches) {
            // Calculate current price to check minimum amount
            const currentPrice = (promotionDetails.currency === 'premium' ? 
              (promotionDetails.planDetails?.premium?.price || 0) : 
              (promotionDetails.planDetails?.standard?.price || 0)) || 0;
            
            // Apply period discount (if applicable) - directly use currentPrice
            const priceWithPeriodDiscount = currentPrice;
            
            // Apply payment type discount
            const priceBeforePromo = paymentType === 'subscription' ?
              priceWithPeriodDiscount * 0.95 : priceWithPeriodDiscount;
              
            // Check minimum amount with EPSILON to handle floating point precision
            const EPSILON = 0.001;
            const meetsMinimum = !response.minimumAmount || 
              priceBeforePromo + EPSILON >= response.minimumAmount;
              
            // Save valid promotion code to localStorage
            localStorage.setItem(PROMO_CODE_STORAGE_KEY, promotionCode);
            
            // Create promotion details to use in state updates
            const newPromotionDetails = {
              isLoaded: true,
              isValid: true, // The code itself is valid, even if it doesn't meet minimum amount
              percentOff: response.percentOff || 0,
              amountOff: response.amountOff || 0,
              couponId: response.couponId || '',
              description: response.description || `Discount: ${response.percentOff ? `${response.percentOff}%` : `฿${response.amountOff}`}`,
              allowedPaymentTypes: response.allowedPaymentTypes || [],
              duration: response.duration || 'once' as 'once' | 'forever' | 'repeating',
              durationInMonths: response.durationInMonths || null,
              minimumAmount: response.minimumAmount || 0,
              currency: response.currency || null,
              // Add error message for minimum amount if applicable but keep isValid true
              errorMessage: !meetsMinimum && response.minimumAmount ? 
                `Your current purchase doesn't meet the minimum amount required (${response.currency || currency}${response.minimumAmount}) for this promotion code.` : 
                undefined
            };
              
            // Update Zustand store with the promotion code and details
            // This is critical to ensure the order summary and price calculations use the updated values
            // Update the promotion code and details via the selector's set function
            useSubscriptionStore.setState({
              promotionCode: promotionCode,
              promotionDetails: newPromotionDetails
            });
              
            // Use a single batch update to prevent multiple re-renders
            startTransition(() => {
              setPromotionDetails(newPromotionDetails);
              
              // Switch to one-time payment for "once" duration coupons
              if (response.duration === 'once') {
                // No longer forcing one-time payment for 'once' duration coupons
                // Give time for state updates to be processed
                setTimeout(() => {
                  // Recalculate price with current payment type
                  const recalculatedPrice = calculatePrice(
                    promotionDetails.currency === 'premium' 
                      ? (promotionDetails.planDetails?.premium?.price || 0)
                      : (promotionDetails.planDetails?.standard?.price || 0)
                  );
                  
                  // Check minimum amount again with updated pricing
                  const EPSILON = 0.001;
                  const meetsMinimumAfterSwitch = !response.minimumAmount || 
                    recalculatedPrice + EPSILON >= response.minimumAmount;
                  
                  // Show appropriate toast after payment type switch is processed
                  if (!hasShownPromoToast) {
                    if (meetsMinimumAfterSwitch) {
                      toast.success(`Promotion code "${promotionCode}" applied: ${response.percentOff ? `${response.percentOff}% off` : `฿${response.amountOff} off`}`);
                      
                      // Update promotion details to remove error message
                      const updatedDetails = {
                        ...newPromotionDetails,
                        errorMessage: undefined
                      };
                      
                      setPromotionDetails(updatedDetails);
                      // Also update the store
                      useSubscriptionStore.setState({
                        promotionDetails: updatedDetails
                      });
                    } else if (response.minimumAmount) {
                      // Update the error message in promotionDetails to match the new calculation
                      const errorMessage = `Your current purchase doesn't meet the minimum amount required (${response.currency || currency}${response.minimumAmount}) for this promotion code.`;
                      const updatedDetails = {
                        ...newPromotionDetails,
                        errorMessage
                      };
                      
                      setPromotionDetails(updatedDetails);
                      // Also update the store
                      useSubscriptionStore.setState({
                        promotionDetails: updatedDetails
                      });
                    }
                    setHasShownPromoToast(true);
                  }
                }, 300); // Small delay to ensure state updates have propagated
              } else {
                // For non-"once" duration coupons, show toast immediately
                if (!hasShownPromoToast) {
                  if (meetsMinimum) {
                    if (response.duration === 'repeating') {
                      toast.success(`Subscription promotion code "${promotionCode}" applied: ${response.percentOff ? `${response.percentOff}% off` : `฿${response.amountOff} off`}${response.durationInMonths ? ` for the first ${response.durationInMonths} ${response.durationInMonths === 1 ? 'month' : 'months'}` : ' (recurring)'}`);
                    } else {
                      toast.success(`Promotion code "${promotionCode}" applied: ${response.percentOff ? `${response.percentOff}% off` : `฿${response.amountOff} off`}`);
                    }
                  }
                  
                  setHasShownPromoToast(true);
                }
              }
            });
          } else {
            // Handle currency mismatch case - mark as completely invalid
            const invalidDetails = {
              isLoaded: true,
              isValid: false,
              percentOff: 0,
              amountOff: 0,
              couponId: '',
              description: 'Invalid promotion code',
              allowedPaymentTypes: [],
              duration: 'once' as 'once' | 'forever' | 'repeating',
              durationInMonths: null,
              minimumAmount: 0,
              currency: response.currency || null,
              errorMessage: `This promotion code is only valid for ${response.currency} currency`
            };
            
            // Update Zustand store
            useSubscriptionStore.setState({
              promotionCode: '',
              promotionDetails: invalidDetails
            });
            
            startTransition(() => {
              setPromotionDetails(invalidDetails);
              
              if (!hasShownPromoToast) {
                toast.error(`Promotion code "${promotionCode}" is only valid for ${response.currency} currency`);
                setHasShownPromoToast(true);
              }
            });
            
            // Remove invalid promotion code from localStorage
            localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
          }
        } else {
          // Use a single batch update for invalid codes
          // Get specific error message from API response if available
          const errorMessage = response?.errorMessage || 'Invalid promotion code';
          const invalidDetails = {
            isLoaded: true,
            isValid: false,
            percentOff: 0,
            amountOff: 0,
            couponId: '',
            description: errorMessage,
            allowedPaymentTypes: [],
            duration: 'once' as 'once' | 'forever' | 'repeating',
            durationInMonths: null,
            minimumAmount: 0,
            currency: null,
            errorMessage
          };
          
          // Update Zustand store
          useSubscriptionStore.setState({
            promotionCode: '',
            promotionDetails: invalidDetails
          });
          
          startTransition(() => {
            setPromotionDetails(invalidDetails);
            
            // Only show error toast if it hasn't been shown yet
            if (!hasShownPromoToast) {
              toast.error(`Invalid promotion code: "${promotionCode}" - ${errorMessage}`);
              setHasShownPromoToast(true);
            }
            
            // Clear invalid promotion code from localStorage
            localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
          });
        }
      } catch (err) {
        console.error('Error fetching promotion details:', err);
        
        if (isMounted) {
          // Fix type error by properly typing the error
          const error = err as Error;
          const errorMessage = error?.message || 'Error validating promotion code';
          
          const errorDetails = {
            isLoaded: true,
            isValid: false,
            percentOff: 0,
            amountOff: 0,
            couponId: '',
            description: errorMessage,
            allowedPaymentTypes: [],
            duration: 'once' as 'once' | 'forever' | 'repeating',
            durationInMonths: null,
            minimumAmount: 0,
            currency: null,
            errorMessage
          };
          
          // Update Zustand store on error
          useSubscriptionStore.setState({
            promotionCode: '',
            promotionDetails: errorDetails
          });
          
          setPromotionDetails(prev => ({
            ...prev,
            ...errorDetails
          }));
          
          // Show error toast
          if (!hasShownPromoToast) {
            toast.error(`Error validating promotion code: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setHasShownPromoToast(true);
          }
          
          // Clear invalid promotion code from localStorage on error
          localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
        }
      } finally {
        // Reset flag when API call completes
        isApiCallInProgress = false;
        
        // Clear global flag when done
        if (isMounted) {
          (window as any).__promotionCodeFetchInProgress = false;
        }
      }
    };
    
    // Execute immediately to avoid delay
    fetchPromotionDetails();
    
    // Cleanup function to prevent updates after unmount
    return () => {
      isMounted = false;
      (window as any).__promotionCodeFetchInProgress = false;
    };
  }, [
    promotionCode,
    promotionDetails.isLoaded,
    currency,
    paymentType,
    hasShownPromoToast,
    hasShownPaymentSwitchToast,
    setHasShownPromoToast,
    setHasShownPaymentSwitchToast,
    updateSubscriptionState,
    subscriptionType
  ]);

  // Function to clear promotion code
  const clearPromotionCode = () => {
    // Clear promotion code and reset related state
    setPromotionCode('');
    setPromotionDetails({
      isLoaded: true,
      isValid: false,
      percentOff: 0,
      amountOff: 0,
      couponId: '',
      description: '',
      allowedPaymentTypes: [],
      duration: 'once',
      durationInMonths: null,
      minimumAmount: 0,
      currency: null
    });
    localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
    
    // Reset toast flags
    setHasShownPromoToast(false);
    setHasShownPaymentSwitchToast(false);
    
    // Reset the payment type warning flag
    (window as any).__hasShownPaymentTypeWarning = false;
    
    // Show feedback
    toast.success('Promotion code cleared');
  };

  // Function to check if the promotion code is valid for the current payment type
  const isPromotionValidForPayment = useCallback(() => {
    if (!promotionDetails.isValid || !promotionDetails.isLoaded) {
      return false;
    }

    // If there are no restrictions on payment types, it's valid for all
    if (!promotionDetails.allowedPaymentTypes || promotionDetails.allowedPaymentTypes.length === 0) {
      return true;
    }

    // Check if the current payment type is in the allowed list
    return promotionDetails.allowedPaymentTypes.includes(paymentType);
  }, [promotionDetails.isValid, promotionDetails.isLoaded, promotionDetails.allowedPaymentTypes, paymentType]);

  // Determine if we have any valid discount (promotion only now)
  const hasValidDiscount = useMemo(() => {
    return (promotionDetails.isValid && promotionDetails.isLoaded && 
            (!promotionDetails.currency || promotionDetails.currency === currency));
  }, [promotionDetails.isValid, promotionDetails.isLoaded, promotionDetails.currency, currency]);

  // Function to get effective promotion code based on all validation checks
  const getEffectivePromotionCode = useCallback((priceBeforePromotion: number) => {
    // Skip if no code to begin with
    if (!promotionCode) {
      return null;
    }
    
    // Skip for trial subscriptions - promotion codes are not allowed
    if (subscriptionType === 'trial') {
      return null;
    }
    
    // Skip if code not valid or not loaded
    if (!promotionDetails.isValid || !promotionDetails.isLoaded) {
      return null;
    }
    
    // Skip if payment type isn't compatible
    if (!isPromotionValidForPayment()) {
      return null;
    }
    
    // Skip if currency doesn't match - this is the missing validation
    if (promotionDetails.currency && promotionDetails.currency !== currency) {
      return null;
    }
    
    // Skip if minimum amount requirement isn't met
    // Use price after other discounts but before promotion code
    const EPSILON = 0.001; // Small tolerance for floating point comparison
    if (promotionDetails.minimumAmount && priceBeforePromotion + EPSILON < promotionDetails.minimumAmount) {
      return null;
    }
    
    // Return the promotion code if it passes all validation
    return promotionCode;
  }, [promotionCode, promotionDetails.isValid, promotionDetails.isLoaded, 
      promotionDetails.minimumAmount, promotionDetails.currency, currency, isPromotionValidForPayment, subscriptionType]);

  return {
    promotionCode,
    setPromotionCode: setPromoCodeWithStorage, // Use the custom setter that updates localStorage
    promotionDetails,
    loading,
    setLoading,
    error,
    setError,
    hasShownPromoToast,
    setHasShownPromoToast,
    hasShownPaymentSwitchToast,
    setHasShownPaymentSwitchToast,
    calculatePrice,
    updateSubscriptionState,
    isPromotionValidForPayment,
    getEffectivePromotionCode,
    isPromotionCodeLoading: !promotionDetails.isLoaded && !!promotionCode,
    clearPromotionCode,
    
    // Function to get the effective discount for payment processing
    getEffectiveDiscount: (priceBeforeDiscount: number) => {
      // Skip for trial subscriptions - promotion codes are not allowed
      if (subscriptionType === 'trial') {
        return 0;
      }
      
      // Skip if currency doesn't match
      if (promotionDetails.currency && promotionDetails.currency !== currency) {
        return 0;
      }
      
      // Check if we have a valid promotion code
      const promoDiscount = isPromotionValidForPayment() ? 
                            (promotionDetails.percentOff > 0 ? 
                              priceBeforeDiscount * (promotionDetails.percentOff / 100) : 
                              promotionDetails.amountOff) : 
                            0;
      
      return promoDiscount;
    },
    
    // Function to check if any discount is valid
    hasValidDiscount,
    
    // Function to get the effective code to send to the API
    getEffectiveCode: useCallback(() => {
      // Skip for trial subscriptions - promotion codes are not allowed
      if (subscriptionType === 'trial') {
        return null;
      }
      
      // If promotion code is valid and loaded, check currency compatibility
      if (promotionDetails.isValid && promotionDetails.isLoaded) {
        // Skip if currency doesn't match
        if (promotionDetails.currency && promotionDetails.currency !== currency) {
          return null;
        }
        
        return { type: 'promotion', code: promotionCode };
      }
      
      return null;
    }, [promotionDetails.isValid, promotionDetails.isLoaded, promotionDetails.currency, currency, promotionCode, subscriptionType])
  };
};